import { useQuery } from '@tanstack/react-query'
import { MEAL_NAMES, MEALS, OTHER_SECTION, type Meal } from '../../shared/meals'
import { api, ApiError } from './api'
import type { Space } from './families'
import type { Place } from './spaces'
import type { Crop } from '../../shared/crop'
import { setCrop, type PreparedPhoto } from './photos'

export type BookSummary = {
  id: string
  title: string
  recipes: number
  cover: string | null
  covers?: string[]
  // Whether you started it, for "Only mine". Missing from an app that loaded before it was sent.
  mine?: boolean
  // Shared in from another circle: read here, added to there. Missing from an older server.
  elsewhere?: boolean
  // From another circle, and here only because it's shared with Everyone. Missing from an older server.
  forEveryone?: boolean
}
// Who a recipe is from, as on the tree: a face to show beside it.
export type From = { id: string; name: string; portrait: string | null }
// book is null for a recipe in no book.
export type RecentRecipe = { id: string; title: string; by: string; book: string | null; cover: string | null }
// The recipes in no book: how many you can see, and how many of them you added.
export type LooseRecipes = { recipes: number; mine: number }
export type Book = {
  id: string
  familyId: string
  title: string
  space: Space | null
  // Who it's for: Everyone, or the places it's shared with that you're in (neither: only its keeper); and whether you
  // can add recipes (only from its own circle). Missing from an older server.
  everyone?: boolean
  sharedWith?: Place[]
  canAdd?: boolean
  mine: boolean
  recipes: { id: string; title: string; by: string; cover: string | null; meals: Meal[]; from?: From | null }[]
}
export type Recipe = {
  id: string
  familyId: string
  title: string
  ingredients: string
  steps: string
  notes: string
  // What it's for: breakfast, dessert... (shared/meals.ts).
  meals: Meal[]
  createdAt: number
  updatedAt: number
  by: string
  byPortrait: string | null
  from: (Omit<From, 'portrait'> & { portrait?: string | null }) | null
  mine: boolean
  // Null when it's in no book (or in one you can't see).
  book: { id: string; title: string } | null
  // Every place it's shared with on its own that you're in, in any of your circles (empty: only whoever added it), and
  // the first of those in its own circle. Missing from an older server.
  sharedWith?: Place[]
  everyone?: boolean
  space?: Space | null
  photos: RecipePhoto[]
  // The photo on its tile (its ref, to match a photo's ref), and the id of the one its keeper picked for it (null: the
  // default, the first photo of the dish).
  cover: string | null
  hero: string | null
}
// A photo of the recipe card itself, or of the dish: the keeper's own. A photo of the dish as someone in the family
// made it is "made".
export type PhotoKind = 'card' | 'food'
// width and height are as it shows (cropped); original is the whole photo, crop the part kept (null for all of it).
// by, addedAt and mine say who added it (missing from an older server).
export type RecipePhoto = {
  id: string
  ref: string
  kind: PhotoKind | 'made'
  width: number
  height: number
  original: { width: number; height: number }
  crop: Crop | null
  by?: string
  byPortrait?: string | null
  addedAt?: number
  mine?: boolean
}
export type RecipeFields = Pick<Recipe, 'title' | 'ingredients' | 'steps' | 'notes' | 'meals'> & {
  fromPersonId?: string | null
  heroPhotoId?: string | null
}
// What a new photo is, sent along with it: its kind, and whether it's the one for the tile.
export type PhotoRole = { kind: PhotoKind | 'made'; hero?: boolean }

// A family's books, all you can see or only those in one space.
export const useBooks = (familyId: string, spaceId?: string) =>
  useQuery({
    queryKey: ['books', familyId, spaceId ?? 'all'],
    queryFn: () =>
      api<{ books: BookSummary[]; recent: RecentRecipe[]; loose?: LooseRecipes }>(
        `/families/${familyId}/books${spaceId ? `?space=${encodeURIComponent(spaceId)}` : ''}`,
      ),
  })

// A photo with its size as shown, for laying it out whole.
export type Shown = { ref: string; width: number; height: number }
// A recipe in the list of all of them: enough for its tile, and for grouping and finding it. The Modern wall also uses
// its tile photo and first card photo with their sizes, who it's from, and its ingredients for "cook with what you
// have" (each missing from an older server).
export type RecipeSummary = {
  id: string
  title: string
  by: string
  createdAt: number
  meals: Meal[]
  // Null when it's in no book.
  book: { id: string; title: string } | null
  cover: string | null
  mine: boolean
  tile?: Shown | null
  card?: Shown | null
  from?: From | null
  ingredients?: string
}

// Every recipe you can see in a family (or one space), newest first; with `mine`, only those you added.
export const useAllRecipes = (familyId: string, mine: boolean, spaceId?: string) =>
  useQuery({
    queryKey: ['books', familyId, 'recipes', spaceId ?? 'all', mine],
    queryFn: () => {
      const query = new URLSearchParams()
      if (spaceId) query.set('space', spaceId)
      if (mine) query.set('mine', '1')
      const rest = query.toString() ? `?${query}` : ''
      return api<{ recipes: RecipeSummary[] }>(`/families/${encodeURIComponent(familyId)}/all-recipes${rest}`)
    },
  })

// The ways to lay out all the recipes: newest first in one run, by name under letters, by meal, or by book.
export type RecipesBy = 'newest' | 'name' | 'meal' | 'book'
export const RECIPES_BY: RecipesBy[] = ['newest', 'name', 'meal', 'book']
export type RecipeGroup = { key: string; label: string; recipes: RecipeSummary[] }

const byTitle = (a: RecipeSummary, b: RecipeSummary) =>
  a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }) || a.id.localeCompare(b.id)

// What someone typed to find a recipe, matched against its name and its book's, ignoring case and accents.
const folded = (text: string) => text.normalize('NFD').replace(/\p{M}/gu, '').toLocaleLowerCase()
export const matching = (recipes: RecipeSummary[], search: string) => {
  const words = folded(search).split(/\s+/).filter(Boolean)
  if (!words.length) return recipes
  return recipes.filter((x) => {
    const text = folded(`${x.title} ${x.book?.title ?? ''}`)
    return words.every((w) => text.includes(w))
  })
}

// All the recipes, grouped for the page. Newest is one group with no heading; by name, a letter for each first letter
// (anything else under #); by meal, a group for each meal in the usual order, a recipe under every meal it's for and
// the untagged ones last; by book, the books A to Z and the recipes in none last. Within a group, recipes run A to Z.
// Empty groups aren't shown.
export function recipeGroups(recipes: RecipeSummary[], by: RecipesBy): RecipeGroup[] {
  if (by === 'newest') return recipes.length ? [{ key: 'all', label: '', recipes }] : []
  const sorted = [...recipes].sort(byTitle)
  const groups = new Map<string, RecipeGroup>()
  const add = (key: string, label: string, recipe: RecipeSummary) => {
    const group = groups.get(key) ?? { key, label, recipes: [] }
    group.recipes.push(recipe)
    groups.set(key, group)
  }
  for (const recipe of sorted) {
    if (by === 'name') {
      const first = folded(recipe.title).charAt(0).toUpperCase()
      const letter = /^[A-Z]$/.test(first) ? first : '#'
      add(letter, letter, recipe)
    } else if (by === 'meal') {
      if (recipe.meals.length === 0) add('', OTHER_SECTION, recipe)
      for (const meal of recipe.meals) add(meal, MEAL_NAMES[meal].section, recipe)
    } else if (recipe.book) {
      add(recipe.book.id, recipe.book.title, recipe)
    } else {
      add('', NO_BOOK, recipe)
    }
  }
  const order =
    by === 'meal'
      ? (g: RecipeGroup) => (g.key ? MEALS.indexOf(g.key as Meal) : MEALS.length)
      : by === 'name'
        ? (g: RecipeGroup) => (g.key === '#' ? 1 : 0)
        : (g: RecipeGroup) => (g.key ? 0 : 1)
  return [...groups.values()].sort(
    (a, b) =>
      order(a) - order(b) ||
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }) ||
      a.key.localeCompare(b.key),
  )
}

export const NO_BOOK = 'Not in a book'

// Sending recipes you added to a book together (null takes them out of their books), in turns the server can take.
export async function sendRecipes(familyId: string, recipes: string[], bookId: string | null) {
  for (let i = 0; i < recipes.length; i += 50) {
    await api(`/families/${encodeURIComponent(familyId)}/recipes/send`, {
      method: 'POST',
      body: { recipes: recipes.slice(i, i + 50), bookId },
    })
  }
}

export const useBook = (id: string) =>
  useQuery({ queryKey: ['book', id], queryFn: () => api<Book>(`/books/${encodeURIComponent(id)}`) })

export const useRecipe = (id: string) =>
  useQuery({ queryKey: ['recipe', id], queryFn: () => api<Recipe>(`/recipes/${encodeURIComponent(id)}`) })

export async function uploadPhoto(recipeId: string, photo: PreparedPhoto, role: PhotoRole) {
  const form = new FormData()
  form.set('kind', role.kind)
  if (role.hero) form.set('hero', '1')
  form.set('original', photo.original)
  form.set('display', photo.display, 'display')
  form.set('thumb', photo.thumb, 'thumb')
  form.set('width', String(photo.width))
  form.set('height', String(photo.height))
  setCrop(form, photo)
  const res = await fetch(`/api/recipes/${recipeId}/photos`, { method: 'POST', body: form, credentials: 'same-origin' })
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { error?: string }
    throw new ApiError(res.status, data.error ?? 'The photo didn’t upload. Please try again.')
  }
}

// The photos of the card itself, of the dish, and of the family making it, each in the order they were added.
export const cardPhotos = (recipe: Pick<Recipe, 'photos'>) => recipe.photos.filter((p) => p.kind === 'card')
export const dishPhotos = (recipe: Pick<Recipe, 'photos'>) => recipe.photos.filter((p) => p.kind === 'food')
export const madePhotos = (recipe: Pick<Recipe, 'photos'>) => recipe.photos.filter((p) => p.kind === 'made')

export type CardReading = Pick<Recipe, 'title' | 'ingredients' | 'steps' | 'notes'>

// "Capture recipe details from image": the display copies of up to four card photos go to be read, and the words
// come back for the form. Nothing is saved until the person saves the recipe.
export async function readCard(familyId: string, images: Blob[]) {
  const form = new FormData()
  for (const image of images.slice(0, 4)) form.append('image', image, 'card')
  const res = await fetch(`/api/families/${encodeURIComponent(familyId)}/card-reading`, {
    method: 'POST',
    body: form,
    credentials: 'same-origin',
  })
  const data = (await res.json().catch(() => ({}))) as Partial<CardReading> & { error?: string }
  if (!res.ok) throw new ApiError(res.status, data.error ?? 'The card couldn’t be read just now.')
  return data as CardReading
}

// Recipe text is kept as typed; for reading and cooking it is split into its lines.
export const lines = (text: string) =>
  text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
